import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppShell } from '../components/AppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, Microphone, Keyboard, Camera, Buildings, ChatCircleText,
} from '@phosphor-icons/react';
import { Loader2, Send, StopCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso.slice(0, 16); }
}

/* -------------------- Quick Text -------------------- */
function QuickText({ chantierId, entrepriseId, onSaved }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/notes`,
        { text: text.trim(), chantier_id: chantierId, entreprise_id: entrepriseId },
        { withCredentials: true });
      setText('');
      toast.success('Note enregistree');
      onSaved && onSaved();
    } catch { toast.error("Impossible d'enregistrer la note"); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex gap-2 items-end" data-testid="ent-quick-text">
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Note liee a cette entreprise..."
        className="min-h-[44px] max-h-[140px] text-sm resize-none flex-1"
        data-testid="ent-text-input"
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); } }}
      />
      <Button onClick={save} disabled={!text.trim() || saving} size="icon"
        className="h-11 w-11 bg-[#FF6B00] hover:bg-orange-700 flex-shrink-0"
        data-testid="ent-text-send">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
      </Button>
    </div>
  );
}

/* -------------------- Quick Voice -------------------- */
function QuickVoice({ chantierId, entrepriseId, onSaved }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const fd = new FormData();
        fd.append('file', blob, 'voice.webm');
        setUploading(true);
        try {
          const params = new URLSearchParams({ chantier_id: chantierId, entreprise_id: entrepriseId });
          await axios.post(`${API}/voice-notes/transcribe?${params.toString()}`, fd, { withCredentials: true });
          toast.success('Note vocale transcrite');
          onSaved && onSaved();
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Erreur de transcription');
        } finally {
          setUploading(false);
          streamRef.current && streamRef.current.getTracks().forEach(t => t.stop());
        }
      };
      mediaRecRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Micro non disponible. Autoriser l'acces au micro.");
    }
  };
  const stop = () => {
    if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') {
      mediaRecRef.current.stop();
      setRecording(false);
    }
  };

  if (uploading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#4B5563]" data-testid="ent-voice-uploading">
        <Loader2 className="h-4 w-4 animate-spin" /> Transcription en cours...
      </div>
    );
  }
  return recording ? (
    <Button onClick={stop} className="w-full h-11 bg-red-600 hover:bg-red-700 text-white"
      data-testid="ent-voice-stop">
      <StopCircle className="h-4 w-4 mr-2" /> Arreter l&apos;enregistrement
    </Button>
  ) : (
    <Button onClick={start} className="w-full h-11 bg-[#FF6B00] hover:bg-orange-700 text-white"
      data-testid="ent-voice-start">
      <Microphone size={18} className="mr-2" weight="bold" /> Note vocale
    </Button>
  );
}

/* -------------------- Quick Photo -------------------- */
function QuickPhoto({ chantierId, entrepriseId, onSaved }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const onPick = () => inputRef.current && inputRef.current.click();
  const onChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const params = new URLSearchParams({ chantier_id: chantierId, entreprise_id: entrepriseId });
      await axios.post(`${API}/upload?${params.toString()}`, fd, { withCredentials: true });
      toast.success('Photo enregistree');
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };
  return (
    <>
      <input type="file" ref={inputRef} accept="image/*" capture="environment"
        onChange={onChange} className="hidden" data-testid="ent-photo-input" />
      <Button onClick={onPick} disabled={uploading}
        className="w-full h-11 bg-[#0055FF] hover:bg-blue-700 text-white"
        data-testid="ent-photo-btn">
        {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Camera size={18} className="mr-2" weight="bold" />}
        {uploading ? 'Envoi...' : 'Photo'}
      </Button>
    </>
  );
}

/* -------------------- Timeline item -------------------- */
function TimelineItem({ item }) {
  const cfg = {
    text:  { label: 'Note',  icon: Keyboard,   bg: 'bg-violet-100', text: 'text-violet-700' },
    voice: { label: 'Vocal', icon: Microphone, bg: 'bg-orange-100', text: 'text-orange-700' },
    photo: { label: 'Photo', icon: Camera,     bg: 'bg-blue-100',   text: 'text-blue-700' },
  }[item.kind] || { label: '-', icon: ChatCircleText, bg: 'bg-gray-100', text: 'text-gray-600' };
  const Icon = cfg.icon;
  return (
    <div className="flex gap-3" data-testid={`ent-timeline-${item.kind}-${item.id}`}>
      <div className="flex flex-col items-center flex-shrink-0 w-8">
        <div className={`w-8 h-8 rounded-full ${cfg.bg} flex items-center justify-center ring-2 ring-white`}>
          <Icon size={14} className={cfg.text} weight="bold" />
        </div>
        <div className="w-px flex-1 bg-gray-200 mt-1" />
      </div>
      <div className="flex-1 pb-4 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={`text-[10px] font-bold uppercase tracking-wide ${cfg.text}`}>{cfg.label}</span>
          <span className="text-xs text-gray-400">{formatDate(item.created_at)}</span>
        </div>
        <Card className="border border-gray-100 shadow-sm">
          <CardContent className="p-3">
            {item.kind === 'photo' && item.storage_path ? (
              <div className="flex items-center gap-3">
                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium">
                  {item.text || 'Photo'}
                </span>
                <a href={`${API}/files/${item.id}/download`}
                  className="text-xs text-[#0055FF] hover:underline"
                  target="_blank" rel="noreferrer" data-testid={`ent-photo-link-${item.id}`}>
                  Ouvrir
                </a>
              </div>
            ) : (
              <p className="text-sm text-[#111827] whitespace-pre-wrap break-words">{item.text || '(vide)'}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* -------------------- Main Page -------------------- */
export default function EntrepriseVuePage() {
  const { chantierId } = useParams();
  const navigate = useNavigate();
  const [chantier, setChantier] = useState(null);
  const [entreprises, setEntreprises] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState({ counts: { text: 0, voice: 0, photo: 0, total: 0 }, items: [] });
  const [tlLoading, setTlLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [chRes, entRes] = await Promise.all([
        axios.get(`${API}/chantiers/${chantierId}`, { withCredentials: true }),
        axios.get(`${API}/chantiers/${chantierId}/entreprises`, { withCredentials: true }),
      ]);
      setChantier(chRes.data);
      const list = (entRes.data || []).filter(e => e && e.id);
      setEntreprises(list);
      if (list.length > 0 && !selected) setSelected(list[0]);
    } catch (_err) {
      toast.error('Impossible de charger les entreprises');
    } finally { setLoading(false); }
  }, [chantierId, selected]);

  useEffect(() => { load(); }, [load]);

  const loadTimeline = useCallback(async (entId) => {
    if (!entId) return;
    setTlLoading(true);
    try {
      const res = await axios.get(
        `${API}/chantiers/${chantierId}/entreprises/${entId}/timeline`,
        { withCredentials: true }
      );
      setTimeline(res.data || { counts: { total: 0 }, items: [] });
    } catch {
      setTimeline({ counts: { text: 0, voice: 0, photo: 0, total: 0 }, items: [] });
    } finally { setTlLoading(false); }
  }, [chantierId]);

  useEffect(() => {
    if (selected && selected.id) loadTimeline(selected.id);
  }, [selected, loadTimeline]);

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-10 h-10 animate-spin text-[#FF6B00]" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-5" data-testid="entreprise-vue-page">
        {/* Header */}
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" className="h-10 w-10"
            onClick={() => navigate(`/chantiers/${chantierId}`)} data-testid="back-btn">
            <ArrowLeft size={22} />
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-[#111827] truncate" data-testid="vue-entreprise-title">
              Vue Entreprise
            </h1>
            <p className="text-sm text-[#4B5563] truncate">
              {chantier?.name || 'Chantier'} — capture rapide par entreprise
            </p>
          </div>
        </div>

        {entreprises.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center text-[#4B5563]" data-testid="ent-empty">
              Aucune entreprise renseignee sur ce chantier. Ajoutez-en dans l&apos;onglet Entreprises du chantier.
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            {/* Sidebar list */}
            <div className="md:col-span-1 space-y-2" data-testid="ent-list">
              {entreprises.map((e) => (
                <button
                  key={e.id}
                  onClick={() => setSelected(e)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    selected && selected.id === e.id
                      ? 'border-[#FF6B00] bg-orange-50 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid={`ent-item-${e.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center border border-gray-200 flex-shrink-0">
                      <Buildings size={18} className="text-[#4B5563]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-[#111827] truncate">{e.nom || 'Entreprise'}</p>
                      {e.corps_etat && <p className="text-xs text-[#6B7280] truncate">{e.corps_etat}</p>}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Right pane: capture + timeline */}
            <div className="md:col-span-2 space-y-4">
              {selected ? (
                <>
                  <Card data-testid="ent-capture-card">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Buildings size={18} className="text-[#FF6B00]" />
                        {selected.nom || 'Entreprise'}
                      </CardTitle>
                      <p className="text-xs text-[#6B7280]">
                        {selected.corps_etat || '-'}
                        {selected.representant ? ` • ${selected.representant}` : ''}
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <QuickText
                        chantierId={chantierId}
                        entrepriseId={selected.id}
                        onSaved={() => loadTimeline(selected.id)}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <QuickVoice
                          chantierId={chantierId}
                          entrepriseId={selected.id}
                          onSaved={() => loadTimeline(selected.id)}
                        />
                        <QuickPhoto
                          chantierId={chantierId}
                          entrepriseId={selected.id}
                          onSaved={() => loadTimeline(selected.id)}
                        />
                      </div>
                      <p className="text-xs text-[#6B7280]">
                        Tout ce que vous ajoutez ici sera lie a cette entreprise et repris dans le compte rendu de reunion.
                      </p>
                    </CardContent>
                  </Card>

                  {/* Counters */}
                  <div className="grid grid-cols-4 gap-2" data-testid="ent-counters">
                    <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
                      <p className="text-xs text-[#6B7280]">Total</p>
                      <p className="text-lg font-bold text-[#111827]">{timeline.counts.total}</p>
                    </div>
                    <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg text-center">
                      <p className="text-xs text-violet-700">Notes</p>
                      <p className="text-lg font-bold text-violet-800">{timeline.counts.text}</p>
                    </div>
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-center">
                      <p className="text-xs text-orange-700">Vocales</p>
                      <p className="text-lg font-bold text-orange-800">{timeline.counts.voice}</p>
                    </div>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
                      <p className="text-xs text-blue-700">Photos</p>
                      <p className="text-lg font-bold text-blue-800">{timeline.counts.photo}</p>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div>
                    <h3 className="text-sm font-semibold text-[#111827] mb-3" data-testid="ent-timeline-title">
                      Historique
                    </h3>
                    {tlLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-[#FF6B00]" />
                      </div>
                    ) : timeline.items.length === 0 ? (
                      <div className="p-4 bg-gray-50 rounded-lg text-center text-sm text-[#6B7280]"
                        data-testid="ent-timeline-empty">
                        Rien pour le moment. Ajoutez une note, un vocal ou une photo ci-dessus.
                      </div>
                    ) : (
                      <div>
                        {timeline.items.map((it) => (
                          <TimelineItem key={`${it.kind}-${it.id}`} item={it} />
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
